import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { FormContainer } from '@eika-kortbetjening/form-container';
import { ErrorCard, SummaryCard } from '@eika/card';
import { Loader } from '@eika/loader';
import { Modal } from '@eika/modal';
import { RichIconTypes } from '@eika/rich-icon';

import { useCloseCreditAccount } from '@/react-query/hooks/mutation/use-close-credit-account';
import { useDebitAccountsQuery } from '@/react-query/hooks/query/accounts/use-debit-accounts';
import { useCard } from '@/react-query/hooks/query/use-card';
import { useCustomerFromAccount } from '@/react-query/hooks/query/use-customer';
import { CloseCreditAccountRequest } from '@/react-query/services/account';

import SummaryCardContent from '@components/close-credit-account/summary/SummaryCardContent';
import SummaryModalContent from '@components/close-credit-account/summary/SummaryModalContent';
import Footer from '@components/footer/Footer';
import { Format, useNavigate } from '@domain/app';
import useText from '@hooks/use-text';
import { isValidAccountNumber } from '@utils/account-utils';
import { prettyPrintProductName } from '@utils/card-utils';
import { goToRoot } from '@utils/url-utils';

import './Summary.scss';

const Summary = () => {
    const { messageErrorText, summarySendIn } = useText();

    const [queryParams] = useSearchParams();
    const accountNumber = queryParams.get('account');
    const reason = queryParams.get('reason');
    const navigate = useNavigate();

    const [open, setOpen] = useState<boolean>(false);

    const { data: card, isPending: cardLoading, isError: cardError } = useCard();
    const { data: debitAccounts, isLoading: accountsLoading, isError: accountsError, error } = useDebitAccountsQuery();
    const selectedAccount = debitAccounts?.find((account) => account.number === accountNumber);
    const { data: customer } = useCustomerFromAccount(selectedAccount);
    const {
        mutate: closeCreditAccount,
        error: closingAccountError,
        isPending: isClosingCreditAccount,
    } = useCloseCreditAccount();

    const isLoading = cardLoading || (isValidAccountNumber(accountNumber) && accountsLoading);
    const isError = cardError || accountsError;

    if (isLoading) {
        return <Loader />;
    }
    if (isError) {
        return <ErrorCard title={error?.message || messageErrorText} />;
    }

    const prettyCardName = prettyPrintProductName(card);
    const prettyCardNumber = Format.cardNumber(card.number);
    const prettyAccountNumber = Format.accountNumber(card.accountNumber);

    const next = () => {
        const closeCreditAccountRequest: CloseCreditAccountRequest = {
            accountToClose: card.accountId,
            reason,
            accountToReceive: selectedAccount?.id,
        };

        closeCreditAccount(closeCreditAccountRequest);
        setOpen(true);
    };

    return (
        <FormContainer>
            <FormContainer.Content>
                <Modal open={open} setOpen={setOpen} contentShouldStretch onClose={() => goToRoot(navigate)}>
                    <SummaryModalContent isPending={isClosingCreditAccount} error={closingAccountError} />
                </Modal>

                <SummaryCard className="close-account-summary-info-card" icon={RichIconTypes.REGNING_M_KAFFEKOPP}>
                    <SummaryCardContent
                        customer={customer}
                        prettyCardName={prettyCardName}
                        prettyCardNumber={prettyCardNumber}
                        prettyAccountNumber={prettyAccountNumber}
                        selectedAccount={selectedAccount}
                    />
                </SummaryCard>
            </FormContainer.Content>
            <FormContainer.Footer>
                <Footer next={next} nextText={summarySendIn} />
            </FormContainer.Footer>
        </FormContainer>
    );
};

export default Summary;
